import React, {useEffect, useMemo, useState} from 'react';
import {Formik, Form, Field} from 'formik';
import {useUser} from "../../../contexts/UserContext";
import form_styles from "../../Form/Form.module.css";
import FieldCustom from "../../Field/FieldCustom";
import styles from "../../../pages/Admin/Admin.module.css";
import AxiosConfig from "../../../services/AxiosConfig";
import FieldUpload from "../../Field/FieldUpload";
import modalSuccess from "../../Modal/ModalSuccess";
import {useModal} from "../../../contexts/ModalContext";
import {Link, useNavigate, useParams, useSearchParams} from "react-router-dom";
import TitleSecondary from "../../Title/TitleSecondary";
import SearchCoach from "../../Search/SearchCoach";
import FieldDateFrequency from "../../Field/FieldDateFrequency";
import FieldPeriodicite from "../../Field/FieldPeriodicite";
import {
    fetchTypesActivite,
    validActiviteModification
} from "../../../services/ActivitesService";
import {generateBreadcrumbSegmentsAdmin} from "../../../services/BreadcrumbService";
import {useBreadcrumb} from "../../../contexts/BreadcrumbContext";
import TitleMain from "../../Title/TitleMain";
import FieldRadio from "../../Field/FieldRadio";
import FieldCheckbox from "../../Field/FieldCheckbox";
import {ReactComponent as AjouterIcone} from "../../../icons/ajouter.svg";
import {ReactComponent as EnregistrerIcone} from "../../../icons/enregistrer.svg";
import {ReactComponent as AnnulerIcone} from "../../../icons/annuler.svg";
import BtnSave from "../../Button/BtnSave";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeftLong, faBuilding, faBuildingUn, faRunning} from "@fortawesome/free-solid-svg-icons";
import {getUtilisateurRoleIcon} from "../../../services/UserService";
import ConsultationCalculator from "../../Form/ConsultationCalculator";
import Routing from "../../../services/routing";
import DateService from "../../../services/DateService";
import ErrorPage from "../../../pages/Erreurs/ErrorPage";

function ActiviteModification({ }) {
    const {user} = useUser();
    const {showModal, hideModal} = useModal();
    const [activite, setActivite] = useState();
    const [entreprise, setEntreprise] = useState();
    const [contrat, setContrat] = useState();
    const { updateDynamicBreadcrumb } = useBreadcrumb();
    const [searchParams] = useSearchParams();
    const mode = searchParams.get('mode') || 'consultation';
    const {entrepriseId, contratId, activiteId} = useParams();
    const formikRef = React.useRef();
    const [typesActivite, setTypesActivite] = useState([]);
    const [coach, setCoach] = useState(null);
    let navigate = useNavigate();
    const [shouldReinitialize, setShouldReinitialize] = useState(false);

    const initialValues = useMemo(() => {
        const now = DateService.formatDateWithoutTime(new Date());

        return {
            debut: activite?.date_start ? DateService.formatDateWithoutTime(activite.date_debut) : now,
            fin: activite?.date_end ? DateService.formatDateWithoutTime(activite.date_fin) : now,
            participants_max: activite?.places_max ?? 30,
            adresse_entreprise: !activite?.lieu?.adresse,
            adresse: activite?.lieu?.adresse ?? '',
            cp: activite?.lieu?.code_postal ?? '',
            ville: activite?.lieu?.ville ?? '',
            lieu_nom: activite?.lieu?.nom ?? '',
            coach: coach ?? '',
            pas_generer_seances: activite?.pas_generer_seances ?? false,
            type: activite?.type?.id ?? "16",
            duree_consultation: activite?.duree_consultation ?? 60,
            nombre_consultations: activite?.duree_consultation
                ? Math.floor(activite.duree_seance / activite.duree_consultation)
                : 1,
            duree_seance: activite?.duree_seance ?? 60,
            periode_inscription: activite?.periode_inscription ?? 30,
            acces_anticipe_nb_jours: activite?.acces_anticipe_nb_jours ?? 0,
            pas_modifier_periodicite: true,
            periodicite: activite?.periodicite ?? 1,
            periodicite_nb: activite?.periodicite_nb ?? 1,
            periodicite_par: activite?.periodicite_par ?? 0,
            periodiciteCombined: activite?.periodiciteCombined ?? 101,
            materiel: activite?.materiel ?? ""
        };
    }, [activite, coach]);

    const handleSuccess = (children = "Note enregistrée avec succès") => {
        showModal('success', {children: children, timeout: 5000});
    };
    const handleError = (children = "Une erreur est survenue") => {
        showModal('error', {children: children, timeout: 5000});
    };

    const handleSubmit = async (values, {setSubmitting}) => {
        const payload = { ...values };

        if (values.pas_modifier_periodicite) {
            delete payload.periodicite;
            delete payload.periodicite_nb;
            delete payload.periodicite_par;
            delete payload.periodiciteCombined;
        }

        const validate = validActiviteModification(payload);

        // Vérifier des valeurs entre payload et initialValues pour retirer du payload les champs non modifiés et éviter de faire des mises à jour inutiles
        for (const key in payload) {
            if (payload[key] === initialValues[key]) {
                delete payload[key];
            }
        }

        const isModified = Object.keys(payload).some(key => {
            if (typeof payload[key] === 'object' && payload[key] !== null) {
                return JSON.stringify(payload[key]) !== JSON.stringify(initialValues[key]);
            }
            return payload[key] !== initialValues[key];
        });

        if (!isModified) {
            handleError("Aucune modification détectée");
            setSubmitting(false);
            return;
        }

        if (!validate.success) {
            console.error("validate.message", validate.message);
            handleError(validate.message);
            setSubmitting(false);
            return;
        }

        try {
            const response = await AxiosConfig.post(`/admin/entreprises/${entrepriseId}/contrats/${contratId}/activites/${activiteId}/edit`, payload);
            if (response.data.success) {
                handleSuccess(response.data.message);
                navigate(`/admin/entreprises/${entrepriseId}/contrats/${contratId}/activites/${response.data.id}`);
            } else {
                handleError(response.data.error);
            }
        } catch (error) {
            console.error(error);
            handleError(error.message);
        } finally {
            setSubmitting(false);
        }
    };

    const submitCoach = (values) => {
        setCoach(values.email);
    }

    const getEntreprise = async () => {
        const response = await AxiosConfig.get(`/admin/entreprises/${entrepriseId}`);
        setEntreprise(response?.data?.myEntreprise);
    }

    const getContrat = async () => {
        const response = await AxiosConfig.get(`/admin/entreprises/${entrepriseId}/contrats/${contratId}`);
        setContrat(response?.data?.myContrat);
    }

    useEffect(() => {
        if (activite && activiteId) {
            setShouldReinitialize(true);
            // Remettre à false après un court délai pour éviter les réinitialisations multiples
            setTimeout(() => setShouldReinitialize(false), 100);
        }
    }, [activite, activiteId]);

    useEffect(() => {
        const getTypesActivite = async () => {
            const response = await fetchTypesActivite();
            setTypesActivite(response.types);
        };

        getTypesActivite();
    }, []);

    useEffect(() => {
        if (activite && activite.coach && activite.coach.email) {
            setCoach(activite.coach.email);
        }
    }, [activite]);

    useEffect(() => {
        if (formikRef.current) {
            formikRef.current.setFieldValue('coach', coach);
        }
    }, [coach]);

    useEffect(() => {
        activiteId ?
        (async () => {
            try {
                const response = await AxiosConfig.get(`/admin/entreprises/${entrepriseId}/contrats/${contratId}/activites/${activiteId}`);

                setContrat(response.data.myContrat);
                setEntreprise(response.data.myEntreprise);
                if (response.data.myActivite) {
                    setActivite(response.data.myActivite);
                }
            } catch (error) {
                console.error('error', error);
            }
        })() :
        getEntreprise();
        getContrat();
    }, [ activiteId ]);

    useEffect(() => {
        updateDynamicBreadcrumb(generateBreadcrumbSegmentsAdmin({ entreprise, contrat, activite }));
    }, [ entreprise, contrat, activite ]);

    if (!entreprise || !contrat) {
        return "Page inaccessible";
    }

    return (
        <>
        { activiteId && <TitleMain text={"Activités"}/> }
            <div style={{margin: '0 0 50px 0'}} className={styles.viewBloc}>
                <TitleSecondary text={activiteId ? "Modification" : "Nouvelle activité"}/>
                <div className={styles.containerColumns + " " + styles.activiteCreationEnDeux}>
                    <div className={styles.formBloc}>
                        <h3>Coach </h3>
                        <SearchCoach onSubmit={submitCoach} act={""}/>
                        <p style={{
                            fontStyle: "italic",
                            fontSize: "var(--fs-secondary)",
                            marginTop: "4px",
                        }}>{coach ? <span style={{ color: "var(--clr-success)" }}>{ coach }</span> : <span style={{ color: "var(--clr-danger)" }}>Pas de coach sélectionné</span>}</p>
                    </div>

                    <div className={styles.formBloc}>
                        <h3>{entreprise?.nom} </h3>
                        <span style={{fontSize: "0.9em", display: "flex", flexDirection: "row", gap: "4px"}}>
                        <FontAwesomeIcon icon={faBuilding}/>
                            {entreprise?.addr} {entreprise?.cp} {entreprise?.ville}
                    </span>
                        <span style={{fontSize: "0.9em", display: "flex", flexDirection: "row", gap: "4px"}}>
                        {getUtilisateurRoleIcon("ROLE_REFERENT")}
                            {contrat?.referent.prenom} {contrat?.referent.nom}
                    </span>
                    </div>
                </div>

                {Object.keys(initialValues).length > 0 ? (
                        <Formik
                            initialValues={initialValues}
                            onSubmit={handleSubmit}
                            enableReinitialize={shouldReinitialize}
                            innerRef={formikRef}
                        >
                            {({isSubmitting, setFieldValue, values}) => {
                                const handleChange = (e) => {
                                    const {name, value} = e.target;
                                    setFieldValue(name, value, false);
                                };

                                return (
                                    <Form>
                                        <ConsultationCalculator />
                                        <div className={styles.containerColumns + " " + styles.activiteCreationEnDeux2}>
                                            <div className={styles.formBloc}>
                                                <h3>Type</h3>
                                                <div style={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                    gap: "6px"
                                                }}>
                                                    <Field as="select" name="type" onChange={handleChange}
                                                           style={{borderRadius: "16px"}}>
                                                        <option>
                                                            -- Sélectionnez un type d'activité --
                                                        </option>
                                                        {typesActivite.map((type, index) => (
                                                            <option key={index} value={type.value.toString()}>
                                                                {type.label}
                                                            </option>
                                                        ))}
                                                    </Field>
                                                    <Link to={Routing.routes.adminActiviteTypesCreation} title={"Créer un type d'activité"}
                                                          className={styles.btnAdd}>
                                                        <AjouterIcone/>
                                                    </Link>
                                                </div>
                                            </div>

                                            <div className={styles.formBloc}>
                                                <h3>Lieu</h3>
                                                <FieldCheckbox name={"adresse_entreprise"}
                                                               label={"Même adresse que l'entreprise"}/>
                                                {!values.adresse_entreprise &&
                                                    <div style={{}}>
                                                        <FieldCustom type={"text"} name={"lieu_nom"} label={"Nom du lieu (ex: Salle de sport, Salle n°14, etc.)"}/>
                                                        <FieldCustom type={"text"} name={"adresse"} label={"Adresse"}/>
                                                        <FieldCustom type={"text"} name={"cp"} label={"Code postal"}/>
                                                        <FieldCustom type={"text"} name={"ville"} label={"Ville"}/>
                                                    </div>}
                                            </div>
                                        </div>

                                        <div className={styles.containerColumns + " " + styles.activiteCreation}>
                                            <div className={styles.formBloc}>
                                                <h3>Dates</h3>
                                                <FieldCustom type={"date"} name={"debut"} label={"Début"}/>
                                                <FieldCustom type={"date"} name={"fin"} label={"Fin"}/>
                                                <FieldCustom type={"number"} name={"periode_inscription"}
                                                             label={"Période d'inscription avant séances (jours)"}/>
                                                <FieldCustom type={"number"} name={"acces_anticipe_nb_jours"}
                                                             label={"Accès anticipé pour ceux n'ayant jamais participé (jours supplémentaires par rapport à période d'inscription)"}/>
                                            </div>
                                            <div className={styles.formBloc}>
                                                <h3>Séances</h3>
                                                <div>
                                                    <FieldCustom type={"number"} name={"participants_max"}
                                                                 label={"Nombre de participants max"}/>
                                                </div>
                                                <div style={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                    justifyContent: "flex-start"
                                                }}>
                                                    <FieldCustom type={"number"} name={"duree_seance"}
                                                                 label={"Durée de la séance (minutes)"}/>
                                                </div>
                                                <div>
                                                    <FieldCustom type={"number"} name={"nombre_consultations"}
                                                                 label={"Nombre de consultations dans la séance"}/>
                                                    <span style={{fontSize: "var(--fs-secondary)", fontStyle: "italic"}}>Durée de chaque consultation : {values.duree_consultation} minutes</span>
                                                    <FieldCustom type={"hidden"} name={"duree_consultation"} readOnly/>
                                                </div>

                                                <div>
                                                    <FieldCustom type={"text"} name={"materiel"}
                                                                 label={"Matériel (optionnel)"}/>
                                                </div>
                                            </div>
                                        </div>

                                        <FieldCheckbox
                                            name={"pas_modifier_periodicite"}
                                            label={"Ne pas modifier la périodicité"}
                                        />
                                        {!values.pas_modifier_periodicite && (
                                            <>

                                        <div className={styles.formBloc + " " + styles.activiteCreationEnDeux}>
                                            <h3>Périodicité</h3>
                                            <p style={{
                                                fontStyle: "italic",
                                                fontSize: "var(--fs-secondary)"
                                            }}>Sélectionner
                                                une fréquence, puis un intervalle. Par exemple, si vous souhaitez que
                                                l'événement se répète toutes les deux semaines, sélectionnez "2" pour
                                                "Répéter tous les" et "semaine(s)" pour la fréquence.
                                                {/*
                                                <br/><br/>
                                                <strong>Option "une fois par"</strong>: Sélectionnez les dates pour une
                                                seule semaine dans le mois ou l'année. Par exemple, "Répéter tous les 2
                                                années une fois par", si la date de début est le 15 janvier 2020,
                                                sélectionnera tous les lundis suivant le 15 janvier tous les deux ans
                                                (par ex. 17 janvier 2022, 15 janvier 2024, etc.).<br/>
                                                <br/>
                                                <strong>Option "complet"</strong>: Sélectionnez toutes les occurrences
                                                dans l'année ou le mois. Par exemple, "Répéter tous les 2 années
                                                complet", si la date de début est le 15 janvier 2020, sélectionnera tous
                                                les lundis de 2022, 2024, etc.
                                                */}
                                            </p>
                                            <FieldPeriodicite name="periodiciteCombined"/>
                                        </div>
                                        <div className={styles.formBloc  + " " + styles.activiteCreationEnDeux2}>
                                            <h3>Créneaux</h3>
                                            <p style={{fontStyle: "italic", fontSize: "var(--fs-secondary)"}}>Choisir une
                                                heure, puis cliquer sur le jour de la semaine pour programmer la séance</p>
                                            <FieldDateFrequency name="frequences"/>
                                        </div>
                                            </>
                                        )}
                                        <div>
                                            <p style={{ textAlign: "center", fontWeight: "500"}}>En modification les séances ne seront pas générées</p>
                                        </div>
                                        <Field id={"coach"} name={"coach"} type={"hidden"}
                                               value={coach ? coach.toString() : ""}/>
                                        <div className={"barre_btn"}>
                                            <button type={"button"} onClick={() => {
                                                navigate(-1)
                                            }} className={"btn btn_danger"}><AnnulerIcone/>Annuler
                                            </button>
                                            <BtnSave isSubmitting={isSubmitting}/>
                                        </div>
                                    </Form>
                                )
                            }
                            }
                        </Formik>
                    ) :
                    (<p>Chargement...</p>)}
            </div>
        </>
    );
}

export default ActiviteModification;